import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import {
  dmsForPersona,
  peoplePool,
  lastMessageOf,
  unreadInContainer,
} from '@/utils/commsHelpers';
import MessageRow from '@/components/comms/MessageRow';
import { holdExpiryHours } from '@/domain/depositWindow';
import DashboardKPITile from '@/components/DashboardKPITile';
import HoldToSign from '@/components/HoldToSign';
import { aud, customerName, hoursLabel, simTimeFull } from '@/utils/format';
import { holdBriefText } from '@/utils/bookingSummary';
import { payAtStudioStateFor } from '@/domain/depositStatus';
import { AWAITING_CLIENT, getOrCreateUniversalLink, pinSelfBookSlot } from '@/domain/selfBookFlow';
import { settlePayouts } from '@/domain/payoutFlow';
import {
  isCommittedBooking,
  isLiveBooking,
  isSolid,
  lifecycleLabel,
  studioDayKey,
} from '@atink/domain';
import { artistResultsFeed } from '@/domain/reportingEngine';
import ArtistSelfBookWeek from '@/components/artist/ArtistSelfBookWeek';
import ArtistWeekSchedule from '@/components/artist/ArtistWeekSchedule';
import ArtistMonthGrid from '@/components/artist/ArtistMonthGrid';
import ArtistDaySchedule from '@/components/queue/ArtistDaySchedule';
import HoldLengthChooser from '@/components/artist/HoldLengthChooser';
import SelfBookStatusList from '@/components/artist/SelfBookStatusList';
import ArtistEarningsDay from '@/components/artist/ArtistEarningsDay';
import { buildArtistMonth } from '@/domain/artistMonth';
import { calendarWeekKeys, nextStudioDayKey } from '@/domain/calendarWeek';
import type { Artist, InternalCommsMessage, InternalDmThread, SelfBookLink } from '@/domain/types';
import { addMonths, format } from 'date-fns';
import {
  Sparkles,
  Wallet,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

type Tab = 'today' | 'tomorrow' | 'week' | 'month' | 'self-book' | 'earnings';

export { calendarWeekKeys };

export default function ArtistDashboard() {
  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const selfBookLinks = useBrainStore((s) => s.selfBookLinks);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const artist = artistId ? artists[artistId] : undefined;
  const deposits = useBrainStore((s) => s.deposits);
  const [tab, setTab] = useState<Tab>('today');
  const [copied, setCopied] = useState(false);
  const [chooserStartIso, setChooserStartIso] = useState<string | null>(null);
  const [showRebookClients, setShowRebookClients] = useState(false);
  // Month tab: how many months off the clock we are paging, and which day's
  // breakdown is open beneath the grid.
  const [monthOffset, setMonthOffset] = useState(0);
  const [pickedDayIso, setPickedDayIso] = useState<string | null>(null);

  const universalLink = useMemo(
    () => Object.values(selfBookLinks).find((l) => l.universal && l.artistId === artistId),
    [selfBookLinks, artistId],
  );

  // The artist's one universal, reusable self-book link. Minted on demand (in an
  // effect, never during render). Re-mints if it's absent so it self-heals the
  // async loadFixtures() boot, which replaces selfBookLinks and would otherwise
  // wipe a link minted a beat earlier.
  useEffect(() => {
    if (artist && !universalLink) {
      getOrCreateUniversalLink({ artistId: artist.id, studioId: artist.studioId });
    }
  }, [artist, universalLink]);

  // Build the client-facing URL for a given link, including pinned params.
  const buildLinkUrl = (l: SelfBookLink | undefined): string => {
    if (!l) return '';
    const q = l.pinnedStartIso
      ? `?d=${l.pinnedStartIso.slice(0, 10)}&t=${new Date(l.pinnedStartIso).getHours()}:00&len=${l.sessionLength}`
      : '';
    return `book.tattoovault.com.au/b/${l.token}${q}`;
  };

  const linkUrl = buildLinkUrl(universalLink);

  const myDeposits = useMemo(() => {
    if (!universalLink) return [];
    const prefix = `${universalLink.depositReference}-`;
    return Object.values(deposits).filter((d) => d.depositReference?.startsWith(prefix));
  }, [deposits, universalLink]);

  // B5 (8xar): the artist's links — named links sent to clients, and the
  // universal link (its pinned hold). Listed from the links, not deposits.
  const myLinks = useMemo(
    () => Object.values(selfBookLinks).filter((l) => l.artistId === artist?.id),
    [selfBookLinks, artist?.id],
  );

  const copyLink = (url: string) => {
    if (!url) return;
    void navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const holdSlot = (
    session: 'small' | 'half-day' | 'full-day',
    custom?: { depositAud: number; durationMin: number },
  ) => {
    if (!universalLink || !chooserStartIso) return;
    // pinSelfBookSlot returns the freshly-pinned link; copy the URL built from THAT
    // (the render-time linkUrl closure is still the pre-pin value this tick).
    const { link: pinned } = pinSelfBookSlot({
      link: universalLink,
      session,
      startIso: chooserStartIso,
      ...(custom ? { custom } : {}),
    });
    setChooserStartIso(null);
    copyLink(buildLinkUrl(pinned));
  };

  // Today / Tomorrow are studio (Melbourne) days, not the browser's day
  // (artists-6): a 7am session read in another zone landed on the wrong tile.
  const todayKey = studioDayKey(clockIso);
  const tomorrowKey = nextStudioDayKey(todayKey);
  // Q16 (tv-2na.11.21): "This week" is the CALENDAR week (Mon-Sun studio days),
  // the same window as the Week tab (ArtistWeekSchedule), not a rolling 7 days.
  const [weekFromKey, weekToKey] = useMemo(() => calendarWeekKeys(clockIso), [clockIso]);

  // The month on show, and the day whose breakdown sits under it. Today's
  // breakdown is open by default; paging to a month without today shows the
  // grid alone until a day is picked.
  const month = useMemo(
    () =>
      artist
        ? buildArtistMonth({
            artist,
            bookings,
            monthIso: addMonths(new Date(clockIso), monthOffset).toISOString(),
            nowIso: clockIso,
          })
        : null,
    [artist, bookings, clockIso, monthOffset],
  );

  const defaultDayIso = month?.days.find((d) => d.isToday && d.kind === 'working')?.dayIso ?? null;
  const selectedDayIso = pickedDayIso ?? defaultDayIso;
  const selectedDay =
    month?.days.find((d) => d.dayIso === selectedDayIso && d.kind === 'working') ?? null;

  // Only live bookings are ever shown: a moved booking's kept original
  // (superseded), a cancelled one or a no-show never reaches the artist's lists
  // (tv-2na.11.14, DECIDED 24 Sep Q8). Holds stay on as faded rows, uncounted.
  const artistBookings = useMemo(
    () =>
      Object.values(bookings)
        .filter((b) => b.artistId === artistId && isLiveBooking(b))
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    [bookings, artistId],
  );

  // Payments the studio manager has sent to this artist's device, awaiting
  // hold-to-sign confirmation (D8 artist side), grouped into the runs they were
  // sent as: ONE payment can cover several work days, so it is confirmed once,
  // naming every day it settles. Subscribes to `payoutRecords` so a run clears the
  // instant `settlePayouts` writes the signed records.
  const runsToConfirm = useMemo(() => {
    if (!artist) return [];
    const sent = Object.values(payoutRecords)
      .filter((r) => r.artistId === artist.id && r.status === 'sent')
      .sort((a, b) => a.dayIso.localeCompare(b.dayIso));
    const byRun = new Map<string, typeof sent>();
    for (const r of sent) {
      const key = r.paymentRunId ?? r.id;
      byRun.set(key, [...(byRun.get(key) ?? []), r]);
    }
    return [...byRun.entries()].map(([id, records]) => ({
      id,
      records,
      totalAud: records.reduce((sum, r) => sum + r.amountDueAud, 0),
      payDayIso: records[0]!.paidOnDayIso ?? records[0]!.dayIso,
    }));
  }, [artist, payoutRecords]);

  // The artist's own live results (never other artists' numbers, never targets).
  const resultsFeed = useMemo(
    () => (artist ? artistResultsFeed(artist.id, clockIso) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artist, clockIso, bookings],
  );

  if (!artist) {
    return <p className="px-10 py-12 text-cream-soft italic">Unknown artist.</p>;
  }

  const todayBookings = artistBookings.filter((b) => studioDayKey(b.start) === todayKey);
  const tomorrowBookings = artistBookings.filter((b) => studioDayKey(b.start) === tomorrowKey);
  const weekBookings = artistBookings.filter((b) => {
    const key = studioDayKey(b.start);
    return key >= weekFromKey && key <= weekToKey;
  });
  // The tiles count SOLID sessions only (Booked, Checked in, In chair, Done).
  const solidCount = (list: typeof artistBookings) => list.filter(isCommittedBooking).length;

  return (
    <section className="px-8 py-8" data-testid={`artist-dashboard-${artist.id}`}>
      <header className="flex items-end justify-between max-w-6xl">
        <div>
          <p className="label-small-caps text-gold-bright">Artist</p>
          <h1 className="heading-display text-3xl text-cream mt-2">{artist.displayName}</h1>
          <p className="font-inter text-xs text-cream-soft mt-2 uppercase tracking-widest">
            {artist.instagramHandle} · {artist.styleSpeciality.join(', ')} · {artist.studioId.replace('-', ' ')}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <DashboardKPITile
            label="Today"
            value={solidCount(todayBookings).toString()}
            accent
            info="Sessions in your chair today."
          />
          <DashboardKPITile
            label="Tomorrow"
            value={solidCount(tomorrowBookings).toString()}
            info="Sessions booked for tomorrow."
          />
          <DashboardKPITile
            label="This week"
            value={solidCount(weekBookings).toString()}
            info="Booked sessions this week, Monday to Sunday. Holds still waiting on a deposit aren't counted."
          />
        </div>
      </header>

      <nav className="mt-6 flex gap-2 flex-wrap border-b border-gold-dim/20 pb-2 max-w-6xl">
        {(
          [
            ['today', 'Today'],
            ['tomorrow', 'Tomorrow'],
            ['week', 'Week'],
            ['month', 'Month'],
            ['self-book', 'Self-book link'],
            ['earnings', 'Earnings'],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            data-testid={`artist-tab-${id}`}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border transition',
              tab === id
                ? 'border-gold-bright text-gold-bright bg-gold/10'
                : 'border-transparent text-cream-soft hover:text-cream hover:border-gold-dim/40',
            )}
          >
            {label}
            {id === 'earnings' && runsToConfirm.length > 0 ? (
              <span
                data-testid="earnings-tab-count"
                className="ml-1.5 inline-flex items-center justify-center rounded-full bg-gold-bright text-ink-black text-[9px] font-semibold h-4 min-w-[1rem] px-1"
              >
                {runsToConfirm.length}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <StudioMessages artist={artist} />

      <div className="mt-6 max-w-6xl">
        {tab === 'today' ? (
          <SessionList
            bookings={todayBookings}
            customers={customers}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
            emptyMessage="No sessions today. The Stripe deposit ripple lands Sarah's Saturday slot, not today."
          />
        ) : null}
        {tab === 'tomorrow' ? (
          <SessionList
            bookings={tomorrowBookings}
            customers={customers}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
            emptyMessage="No sessions scheduled for tomorrow."
          />
        ) : null}
        {tab === 'week' ? (
          <ArtistWeekSchedule
            artist={artist}
            bookings={artistBookings}
            customers={customers}
            deposits={deposits}
            onOpen={(b) => navigate(`/in-chair-rebooking/${b.id}`)}
          />
        ) : null}
        {tab === 'month' && month ? (
          <div className="max-w-5xl" data-testid="artist-month-tab">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 className="heading-serif text-base text-cream">
                  {monthOffset === 0 ? 'This month' : format(new Date(month.monthStartIso), 'MMMM yyyy')}
                </h3>
                <p className="mt-1 font-inter text-[11px] uppercase tracking-widest text-cream-faint">
                  Number in the cell = hours open · strip under it = where those hours sit in the day
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="month-prev"
                  onClick={() => setMonthOffset((o) => o - 1)}
                  aria-label="Previous month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold-dim/40 text-cream-soft transition hover:border-gold-bright hover:text-gold-bright"
                >
                  <ChevronLeft size={15} />
                </button>
                <span
                  data-testid="month-range"
                  className="min-w-[8.5rem] px-2 text-center font-mono text-sm text-cream tabular-nums"
                >
                  {format(new Date(month.monthStartIso), 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  data-testid="month-next"
                  onClick={() => setMonthOffset((o) => o + 1)}
                  aria-label="Next month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold-dim/40 text-cream-soft transition hover:border-gold-bright hover:text-gold-bright"
                >
                  <ChevronRight size={15} />
                </button>
                {monthOffset !== 0 ? (
                  <button
                    type="button"
                    data-testid="month-this"
                    onClick={() => setMonthOffset(0)}
                    className="ml-1 rounded-full border border-gold-dim/40 px-3 py-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft transition hover:text-gold-bright"
                  >
                    This month
                  </button>
                ) : null}
              </div>
            </div>

            <ArtistMonthGrid
              month={month}
              selectedDayIso={selectedDay?.dayIso ?? null}
              onSelect={setPickedDayIso}
            />

            {selectedDay ? (
              <div className="mt-5">
                <ArtistDaySchedule artistId={artist.id} dayIso={selectedDay.dayIso} />
              </div>
            ) : (
              <p className="mt-5 font-serif text-sm text-cream-faint" data-testid="month-no-day">
                Pick a working day to see how it breaks down.
              </p>
            )}
          </div>
        ) : null}
        {tab === 'self-book' ? (
          <div className="panel p-6 max-w-xl" data-testid="artist-self-book-panel">
            <h3 className="heading-serif text-base text-cream mb-1">Your self-book link</h3>
            <p className="font-serif text-cream-soft text-sm mb-4">
              Tap an open time to hold it. Your link pins to that day, time and length, and copies itself.
            </p>

            <ArtistSelfBookWeek
              artist={artist}
              heldStartIso={universalLink?.pinnedStartIso ?? null}
              onHold={(iso) => setChooserStartIso(iso)}
            />

            {universalLink?.pinnedStartIso ? (
              <p
                className="mt-4 rounded-md border border-gold-bright/50 bg-gold/10 px-3 py-2 font-serif text-xs text-cream"
                data-testid="held-slot-summary"
              >
                Holding {simTimeFull(universalLink.pinnedStartIso)} · {universalLink.sessionLength}. Held for the
                client for {holdExpiryHours(universalLink.pinnedStartIso, clockIso)}h, then it expires to a
                findable record.
              </p>
            ) : null}

            <p className="label-small-caps mt-5 mb-2">Your link</p>
            <div className="flex items-stretch rounded-md border border-gold-dim/40 overflow-hidden">
              <span className="flex-1 font-mono text-sm text-cream px-3 py-3 bg-ink-black truncate">
                {linkUrl}
              </span>
              <button
                type="button"
                data-testid="copy-self-book-link"
                onClick={() => copyLink(linkUrl)}
                className="btn-primary rounded-none px-5 text-xs flex items-center gap-1.5"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="font-mono text-[10px] text-cream-faint mt-2 break-all" data-testid="self-book-raw-link">
              {linkUrl}
            </p>

            {universalLink ? (
              <p className="font-mono text-xs text-cream-soft mt-4">
                Deposit reference{' '}
                <span className="text-gold-bright tracking-widest">{universalLink.depositReference}</span>{' '}
                · a unique number is stamped on every held slot.
              </p>
            ) : null}
            <button
              type="button"
              data-testid="preview-self-book-page"
              onClick={() => universalLink && navigate(`/customer-book/${universalLink.token}`)}
              className="btn-ghost text-xs mt-4"
            >
              Preview customer page
            </button>

            <p className="label-small-caps mt-6 mb-2">Sent links</p>
            <SelfBookStatusList
              deposits={myDeposits}
              customers={customers}
              links={myLinks}
              depositsById={deposits}
            />

            {chooserStartIso ? (
              <HoldLengthChooser
                artistName={artist.displayName}
                startIso={chooserStartIso}
                onPick={holdSlot}
                onClose={() => setChooserStartIso(null)}
              />
            ) : null}
          </div>
        ) : null}
        {tab === 'earnings' ? (
          <div className="space-y-4 max-w-3xl">
            {runsToConfirm.length > 0 ? (
              <div
                className="panel p-4 border border-gold-bright/40 bg-gold-bright/5"
                data-testid="payouts-to-confirm"
              >
                <p className="label-small-caps text-gold-bright">
                  Payment{runsToConfirm.length === 1 ? '' : 's'} to confirm
                </p>
                <p className="font-serif text-cream-soft text-xs mt-1">
                  Your studio manager has handed the cash over. Hold to confirm you&rsquo;ve received it.
                </p>
                <div className="mt-3 space-y-3">
                  {runsToConfirm.map((run) => (
                    <div
                      key={run.id}
                      className="panel-dark p-3 border border-gold-dim/25"
                      data-testid={`payout-confirm-row-${artist.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="heading-serif text-lg text-cream">{aud(run.totalAud)}</p>
                          <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
                            {artist.studioId.replace('-', ' ')} ·{' '}
                            {run.records.length === 1 ? 'one day' : `${run.records.length} days`}
                          </p>
                        </div>
                      </div>
                      {/* Every work day this one payment settles, so a bundle is
                          never a mystery total: each day is listed, paid whole. */}
                      <table className="w-full text-sm mt-2.5">
                        <tbody>
                          {run.records.map((r) => (
                            <tr key={r.dayIso} data-testid={`payout-confirm-day-${r.dayIso}`}>
                              <td className="py-1 font-mono text-xs text-cream-soft">
                                {format(new Date(`${r.dayIso}T12:00:00+10:00`), 'EEE d MMM')}
                                {r.dayIso !== run.payDayIso ? (
                                  <span className="text-cream-faint italic ml-2">carried</span>
                                ) : null}
                              </td>
                              <td className="py-1 text-right font-mono text-xs text-cream">
                                {aud(r.amountDueAud)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-3">
                        <HoldToSign
                          testid={`payout-confirm-hold-${artist.id}`}
                          label="Hold to confirm receipt"
                          holdingLabel="Keep holding to confirm…"
                          onSigned={() => {
                            // The artist only ever confirms money actually handed
                            // over. Whether a short day was covered or deferred is
                            // the manager's decision, made before it was sent. One
                            // hold signs for every day in the payment.
                            settlePayouts({
                              studioId: artist.studioId,
                              artistId: artist.id,
                              dayIsos: run.records.map((r) => r.dayIso),
                              payDayIso: run.payDayIso,
                              signedByName: artist.displayName,
                              // The manager already decided this money was payable
                              // when they sent it; the artist's hold records receipt,
                              // it does not re-litigate the drawer.
                              allowShortDrawer: true,
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-4" data-testid="artist-results-tiles">
              <DashboardKPITile
                label="Appointments this week"
                value={(resultsFeed?.appointmentsThisWeek ?? 0).toString()}
                accent
                info="Your booked sessions this week, Monday to Sunday (holds still waiting on a deposit aren't counted). These are your numbers only — no one else's."
              />
              <DashboardKPITile
                label="Rebooks this week"
                value={(resultsFeed?.rebooksThisWeek ?? 0).toString()}
                info="Clients you rebooked in the chair this week (Monday to Sunday) — the next session locked in before they left. Tap ‘See clients’ below to view who."
              />
              <DashboardKPITile
                label="Rebook streak"
                value={(resultsFeed?.rebookStreak ?? 0).toString()}
                info="How many of your most recent finished sessions rebooked in a row. It resets the first time a session does not rebook."
              />
            </div>
            {resultsFeed && resultsFeed.rebookClients.length > 0 ? (
              <div className="panel-dark p-3" data-testid="artist-rebook-clients">
                <button
                  type="button"
                  data-testid="artist-rebook-clients-toggle"
                  onClick={() => setShowRebookClients((v) => !v)}
                  className="text-xs uppercase tracking-widest text-gold-bright hover:text-cream transition"
                >
                  {showRebookClients ? 'Hide clients' : `See clients (${resultsFeed.rebookClients.length})`}
                </button>
                {showRebookClients ? (
                  <ul className="mt-3 space-y-1">
                    {resultsFeed.rebookClients.map((c) => (
                      <li key={c.bookingId} className="font-inter text-sm text-cream-soft" data-testid={`artist-rebook-client-${c.bookingId}`}>
                        {c.name}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <ArtistEarningsDay artist={artist} dayIso={clockIso} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * A compact "Messages from the studio" card at the top of the artist's home. Ops
 * escalations (tv-6qv.28 — "ask the artist to reach out") land in the artist's
 * internal-comms DMs, but the artist never opens /comms unprompted, so the ask went
 * unseen. This surfaces the latest message of each of the artist's DM threads,
 * newest first, with an unread dot, right where they already are. It reuses the
 * comms MessageRow so it reads identically to the full comms screen, and links out
 * to /comms (deep-linked per thread) for the reply loop. Renders nothing when the
 * artist has no DMs, so it never disturbs an artist with an empty inbox.
 */
function StudioMessages({ artist }: { artist: Artist }) {
  const navigate = useNavigate();
  const internalDmThreads = useBrainStore((s) => s.internalDmThreads);
  const agents = useBrainStore((s) => s.agents);
  const artists = useBrainStore((s) => s.artists);
  const lastReadByChannel = useSessionStore((s) => s.lastReadByChannel);

  const peopleById = useMemo(
    () => new Map(peoplePool(agents, artists).map((p) => [p.id, p])),
    [agents, artists],
  );

  // One row per thread — its latest message — sorted newest-first. Threads with no
  // messages are dropped so the card only ever shows something to read.
  const rows = useMemo(() => {
    return dmsForPersona(artist.id, internalDmThreads)
      .map((thread) => ({ thread, last: lastMessageOf(thread) }))
      .filter(
        (r): r is { thread: InternalDmThread; last: InternalCommsMessage } => r.last !== undefined,
      )
      .sort((a, b) => b.last.timestamp.localeCompare(a.last.timestamp));
  }, [artist.id, internalDmThreads]);

  if (rows.length === 0) return null;

  return (
    <div
      className="panel p-4 border border-gold-bright/40 bg-gold-bright/5 max-w-6xl mt-6"
      data-testid="artist-studio-messages"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="label-small-caps text-gold-bright">Messages from the studio</p>
          <p className="font-serif text-cream-soft text-xs mt-1">
            Ops may ask you to reach out to a client. Tap through to reply in comms.
          </p>
        </div>
        <button
          type="button"
          data-testid="artist-studio-messages-open-comms"
          onClick={() => navigate('/comms')}
          className="btn-ghost text-xs shrink-0"
        >
          Open in comms
        </button>
      </div>
      <ul className="mt-3 space-y-1">
        {rows.map(({ thread, last }) => {
          const unread = unreadInContainer(thread, lastReadByChannel[thread.id], artist.id) > 0;
          return (
            <li
              key={thread.id}
              data-testid={`artist-studio-message-${thread.id}`}
              className="flex items-center gap-2"
            >
              <span
                data-testid={unread ? `artist-studio-unread-${thread.id}` : undefined}
                aria-label={unread ? 'Unread' : undefined}
                className={clsx(
                  'h-2 w-2 shrink-0 rounded-full',
                  unread ? 'bg-gold-bright' : 'bg-transparent',
                )}
              />
              <div className="flex-1 min-w-0">
                <MessageRow
                  message={last}
                  peopleById={peopleById}
                  showThreadAffordance={false}
                  selfPersonId={artist.id}
                  compact
                />
              </div>
              <button
                type="button"
                data-testid={`artist-studio-reply-${thread.id}`}
                onClick={() => navigate(`/comms?dm=${thread.id}`)}
                className="btn-ghost text-xs shrink-0"
              >
                Reply
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SessionList({
  bookings,
  customers,
  onOpen,
  emptyMessage,
}: {
  bookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  onOpen: (b: ReturnType<typeof useBrainStore.getState>['bookings'][string]) => void;
  emptyMessage: string;
}) {
  const deposits = useBrainStore((s) => s.deposits);
  if (bookings.length === 0) {
    return <p className="font-serif text-cream-soft">{emptyMessage}</p>;
  }
  return (
    <ol className="space-y-3">
      {bookings.map((b) => {
        const cust = customers[b.customerId];
        // A hold (held / pending deposit) is not a solid booking: faded, uncounted.
        const isHold = !isCommittedBooking(b);
        return (
          <li
            key={b.id}
            data-testid={`artist-session-${b.id}`}
            data-hold={isHold ? 'true' : 'false'}
            className={clsx('panel p-4', isHold && 'opacity-50')}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-inter text-lg text-cream">
                  {cust
                    ? customerName(cust)
                    : b.customerId === AWAITING_CLIENT
                      ? 'Awaiting client'
                      : b.customerId}
                </p>
                <p className="font-inter text-xs text-cream-soft mt-1 uppercase tracking-widest">
                  {simTimeFull(b.start)} · {hoursLabel(b.durationMin)} · {b.sessionLength}
                </p>
                <p className="font-serif text-sm text-cream-soft mt-3">{holdBriefText(b.designBrief, payAtStudioStateFor(b, deposits))}</p>
                {b.referencePhotos.length > 0 ? (
                  <div className="flex gap-2 mt-3">
                    {b.referencePhotos.slice(0, 3).map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt="reference"
                        loading="lazy"
                        className="h-14 w-14 object-cover rounded border border-gold-dim/30"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2">
                {/* The lifecycle label (W4): a paid/solid booking is "Booked";
                    "Confirmed" is only the confirmation-call outcome. */}
                <span
                  data-testid={`artist-session-status-${b.id}`}
                  className={clsx('text-[10px]', isSolid(b) ? 'pill-mint' : 'pill-cream')}
                >
                  {isSolid(b) ? <Wallet size={10} /> : null} {lifecycleLabel(b)}
                </span>
                <button
                  type="button"
                  onClick={() => onOpen(b)}
                  className="btn-ghost text-xs"
                >
                  <Sparkles size={10} /> Open record
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

